import { Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { Link, useLocation } from "@tanstack/react-router";

const TabLink = styled(Link, {
  shouldForwardProp: (prop) =>
    !["isActive", "disabled"].includes(prop as string),
})<{
  isActive: boolean;
  disabled?: boolean;
}>(({ isActive, disabled }) => ({ theme }) => ({
  display: "flex",
  padding: "9px 16px",
  alignItems: "center",
  gap: "10px",
  borderRadius: "4px",
  border: "none",
  backgroundColor:
    isActive && !disabled ? "#E9EAFF" : theme.palette.support[50],
  fontFamily: '"Open Sans", sans-serif',
  fontSize: "14px",
  cursor: disabled ? "not-allowed" : "pointer",
  textDecoration: "none",
  transition: "all 0.2s ease-in-out",
  opacity: disabled ? 0.6 : 1,
  pointerEvents: disabled ? "none" : "auto",
  "&:hover": {
    backgroundColor: disabled
      ? theme.palette.support[50]
      : isActive
        ? theme.palette.primary.light
        : theme.palette.action.hover,
  },
  ".MuiTypography-root": {
    fontWeight: isActive
      ? theme.typography.fontWeightSemiBold
      : theme.typography.fontWeightRegular,
    color: disabled
      ? theme.palette.text.disabled
      : isActive || !disabled
        ? theme.palette.secondary.dark
        : theme.palette.text.disabled,
  },
}));

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
  route?: string;
}

interface TabNavigationProps {
  tabs: TabItem[];
}

export function TabNavigation({ tabs }: TabNavigationProps) {
  const location = useLocation();

  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {tabs.map(({ id, route, label, disabled }) => (
        <TabLink
          key={id}
          to={route}
          disabled={disabled}
          isActive={location.pathname === route}
        >
          <Typography variant="body2">{label}</Typography>
        </TabLink>
      ))}
    </Stack>
  );
}
